import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import Swal from 'sweetalert2';
import { useNotifications } from '../contexts/NotificationsContext';


const FIELD_LABELS = {
    firstName: 'First Name',
    lastName: 'Last Name',
    phoneNumber: 'Phone Number',
    birthday: 'Birthday',
    address: 'Address',
    city: 'City',
    state: 'State/Province',
    zipCode: 'Zip Code',
    country: 'Country',
    profilePictureUrl: 'Profile Picture',
};

const REQUIRED_PROFILE_FIELDS = [
    'firstName',
    'lastName',
    'phoneNumber',
    'birthday',
    'address',
    'city',
    'country',
];

function formatFieldValue(field, value) {
    if (!value) return '(empty)';
    if (field === 'birthday') return new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    if (field === 'profilePictureUrl') return '(image)';
    return value;
}

const ProfileReminderCard = ({ missingFields, onGoToProfile, onDismiss }) => (
    <button
        onClick={onGoToProfile}
        className="w-full text-left px-3 py-3 sm:px-5 sm:py-4 border-b border-amber-100 bg-amber-50 hover:bg-amber-100 transition-colors group"
    >
        <div className="flex items-start gap-2 sm:gap-3">
            <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-amber-200 flex items-center justify-center shrink-0 mt-0.5">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                    <line x1="12" y1="11" x2="12" y2="16" />
                    <line x1="12" y1="19" x2="12.01" y2="19" />
                </svg>
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-0.5">
                    <p className="text-sm font-semibold text-amber-800">Complete your profile</p>
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={onDismiss}
                        onKeyDown={e => e.key === 'Enter' && onDismiss(e)}
                        className="text-amber-400 hover:text-amber-600 transition-colors ml-2 shrink-0 cursor-pointer"
                        title="Dismiss for this session"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </span>
                </div>
                <p className="text-xs text-amber-700 leading-relaxed">
                    {missingFields.length === 1
                        ? `Your ${FIELD_LABELS[missingFields[0]]} is missing. Tap to fill it in.`
                        : `${missingFields.length} fields are still empty — ${missingFields.slice(0, 2).map(f => FIELD_LABELS[f]).join(', ')}${missingFields.length > 2 ? ` & ${missingFields.length - 2} more` : ''}. Tap to complete.`
                    }
                </p>
                <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-amber-200 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-amber-500 rounded-full transition-all"
                            style={{ width: `${Math.round(((REQUIRED_PROFILE_FIELDS.length - missingFields.length) / REQUIRED_PROFILE_FIELDS.length) * 100)}%` }}
                        />
                    </div>
                    <span className="text-[10px] font-semibold text-amber-600 whitespace-nowrap">
                        {REQUIRED_PROFILE_FIELDS.length - missingFields.length}/{REQUIRED_PROFILE_FIELDS.length}
                    </span>
                </div>
                <p className="text-[11px] font-medium text-amber-600 mt-1.5 group-hover:underline">Go to Profile →</p>
            </div>
        </div>
    </button>
);

export function NotificationBell({ onGoToProfile, newOrderNotifs = [], onClearOrderNotifs, onGoToOrders, onGoToInventory }) {
    const [isOpen, setIsOpen] = useState(false);
    const [priceRequests, setPriceRequests] = useState([]);
    const [adminMenuRequests, setAdminMenuRequests] = useState([]);
    const [staffNotifs, setStaffNotifs] = useState([]);
    const [staffAdminRequestNotifs, setStaffAdminRequestNotifs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [missingFields, setMissingFields] = useState([]);
    const [profileDismissed, setProfileDismissed] = useState(false);
    const [dismissedOrderIds, setDismissedOrderIds] = useState(new Set());
    const [showAllStaffNotifs, setShowAllStaffNotifs] = useState(false);
    const [clickedNotifIds, setClickedNotifIds] = useState(() => new Set(JSON.parse(localStorage.getItem('clickedNotifIds') || '[]')));
    const markNotifClicked = (id) => setClickedNotifIds(prev => {
        const next = new Set(prev).add(id);
        localStorage.setItem('clickedNotifIds', JSON.stringify([...next]));
        return next;
    });
    const panelRef = useRef(null);
    const { staffAdjustmentRequests, staffAdjustmentResolutions, staffCountAlerts, staffEODAlerts, staffNewOrders, staffRemakeAlerts, adminLowStockAlerts, dismissStockCountAlert, dismissAdjustmentNotif, dismissAdjustmentResolution, dismissEODAlert, dismissNewOrderNotif, dismissLowStockAlert, dismissRemakeAlert,
        clearAllStockCountAlerts, clearAllEODAlerts, clearAllAdjustmentRequests, clearAllAdjustmentResolutions, clearAllNewOrderNotifs, clearAllLowStockAlerts, clearAllRemakeAlerts, navigateToInventory, navigateToMenuManagement, navigateToOrders, navigateToShiftAttendance } = useNotifications();

    const userType = sessionStorage.getItem('userType');
    const isSuperAdmin = userType === 'admin';

    const checkProfileCompletion = useCallback(async () => {
        try {
            const userEmail = sessionStorage.getItem('userEmail');
            if (!userEmail) return;

            let missing = [];

            if (isSuperAdmin) {
                const { data: adminData } = await supabase
                    .from('admin_accounts')
                    .select('id')
                    .eq('email', userEmail)
                    .single();
                if (!adminData) return;

                const { data: profile } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('user_id', adminData.id)
                    .eq('user_type', 'admin')
                    .maybeSingle();

                const mapped = profile ? {
                    firstName: profile.first_name,
                    lastName: profile.last_name,
                    phoneNumber: profile.phone_number,
                    birthday: profile.birthday,
                    address: profile.address,
                    city: profile.city,
                    country: profile.country,
                } : {};

                missing = REQUIRED_PROFILE_FIELDS.filter(
                    f => !mapped[f] || mapped[f].toString().trim() === ''
                );
            } else {
                const { data: staffData, error: staffErr } = await supabase
                    .from('staff')
                    .select('id')
                    .or(`email.eq.${userEmail},company_email.eq.${userEmail}`)
                    .maybeSingle();
                if (staffErr || !staffData) return;

                const { data: profile } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('user_id', staffData.id)
                    .eq('user_type', 'staff')
                    .maybeSingle();

                const mapped = profile ? {
                    firstName: profile.first_name,
                    lastName: profile.last_name,
                    phoneNumber: profile.phone_number,
                    birthday: profile.birthday,
                    address: profile.address,
                    city: profile.city,
                    country: profile.country,
                } : {};

                missing = REQUIRED_PROFILE_FIELDS.filter(
                    f => !mapped[f] || mapped[f].toString().trim() === ''
                );
            }

            setMissingFields(missing);

            if (missing.length > 0) {
                const wasDismissed = sessionStorage.getItem('profileReminderDismissed');
                if (!wasDismissed) setProfileDismissed(false);
            }
        } catch (err) {
            console.error('Profile completion check error:', err);
        }
    }, [isSuperAdmin]);


    const fetchPendingPriceRequests = useCallback(async () => {
        if (!isSuperAdmin) return;
        try {
            const { data, error } = await supabase
                .from('menu_price_change_requests')
                .select('*')
                .eq('status', 'pending')
                .order('created_at', { ascending: false });
            if (error) throw error;

            const dismissed = JSON.parse(localStorage.getItem('dismissedPriceRequestNotifs') || '[]');
            setPriceRequests((data || []).filter(r => !dismissed.includes(r.id)));
        } catch (err) {
            console.error('Error fetching price change requests:', err);
        }
    }, [isSuperAdmin]);

    const dismissPriceRequestNotif = (id) => {
        const dismissed = JSON.parse(localStorage.getItem('dismissedPriceRequestNotifs') || '[]');
        if (!dismissed.includes(id)) dismissed.push(id);
        localStorage.setItem('dismissedPriceRequestNotifs', JSON.stringify(dismissed));
        setPriceRequests(prev => prev.filter(n => n.id !== id));
    };

    const dismissAllPriceRequests = () => {
        const ids = priceRequests.map(r => r.id);
        const dismissed = JSON.parse(localStorage.getItem('dismissedPriceRequestNotifs') || '[]');
        localStorage.setItem('dismissedPriceRequestNotifs', JSON.stringify([...dismissed, ...ids]));
        setPriceRequests([]);
    };

    const fetchPendingAdminRequests = useCallback(async () => {
        if (!isSuperAdmin) return;
        try {
            const { data, error } = await supabase
                .from('admin_requests')
                .select('id, request_type, item_type, item_id, item_name, reason, branch_id, status, created_at, branches:branch_id(name)')
                .eq('status', 'pending')
                .in('request_type', ['add', 'delete', 'reinstate'])
                .in('item_type', ['menu', 'variant', 'modifier'])
                .order('created_at', { ascending: false });
            if (error) throw error;

            const dismissed = JSON.parse(localStorage.getItem('dismissedAdminRequestNotifs') || '[]');
            setAdminMenuRequests((data || []).filter(r => !dismissed.includes(r.id)));
        } catch (err) {
            console.error('Error fetching admin requests:', err);
        }
    }, [isSuperAdmin]);

    const dismissAdminRequestNotif = (id) => {
        const dismissed = JSON.parse(localStorage.getItem('dismissedAdminRequestNotifs') || '[]');
        if (!dismissed.includes(id)) dismissed.push(id);
        localStorage.setItem('dismissedAdminRequestNotifs', JSON.stringify(dismissed));
        setAdminMenuRequests(prev => prev.filter(n => n.id !== id));
    };

    const dismissAllAdminRequests = () => {
        const ids = adminMenuRequests.map(r => r.id);
        const dismissed = JSON.parse(localStorage.getItem('dismissedAdminRequestNotifs') || '[]');
        localStorage.setItem('dismissedAdminRequestNotifs', JSON.stringify([...dismissed, ...ids]));
        setAdminMenuRequests([]);
    };

    const fetchStaffNotifs = useCallback(async () => {
        if (isSuperAdmin) return;
        try {
            const userEmail = sessionStorage.getItem('userEmail');
            if (!userEmail) return;

            const { data: staffData, error: staffError } = await supabase
                .from('staff')
                .select('id')
                .or(`email.eq.${userEmail},company_email.eq.${userEmail}`)
                .maybeSingle();
            if (staffError || !staffData) return;

            const dismissed = JSON.parse(localStorage.getItem('dismissedNotifs') || '[]');

            const { data, error } = await supabase
                .from('credential_change_requests')
                .select('*')
                .eq('requester_id', staffData.id)
                .eq('requester_type', 'staff')
                .in('status', ['approved', 'rejected'])
                .order('reviewed_at', { ascending: false })
                .limit(10);

            if (error) throw error;

            const unread = (data || []).filter(r => !dismissed.includes(r.id));
            setStaffNotifs(unread);
        } catch (err) {
            console.error('Error fetching staff notifs:', err);
        }
    }, [isSuperAdmin]);

    const fetchStaffAdminRequestNotifs = useCallback(async () => {
        if (isSuperAdmin) return;
        try {
            const userEmail = sessionStorage.getItem('userEmail');
            if (!userEmail) return;

            const { data: staffData, error: staffError } = await supabase
                .from('staff')
                .select('id, branch_id, role')
                .or(`email.eq.${userEmail},company_email.eq.${userEmail}`)
                .maybeSingle();
            if (staffError || !staffData?.branch_id || staffData.role !== 'Admin') return;

            const dismissed = JSON.parse(localStorage.getItem('dismissedAdminReqNotifs') || '[]');

            const { data, error } = await supabase
                .from('admin_requests')
                .select('id, request_type, item_type, item_name, status, rejection_reason, branch_id, created_at')
                .eq('branch_id', staffData.branch_id)
                .in('item_type', ['menu', 'variant', 'modifier'])
                .in('request_type', ['add', 'delete', 'reinstate'])
                .in('status', ['approved', 'rejected'])
                .order('created_at', { ascending: false })
                .limit(10);

            if (error) throw error;

            const unread = (data || []).filter(r => !dismissed.includes(r.id));
            setStaffAdminRequestNotifs(unread);
        } catch (err) {
            console.error('Error fetching staff admin request notifs:', err);
        }
    }, [isSuperAdmin]);



    useEffect(() => {
        checkProfileCompletion();
        if (isSuperAdmin) {
            fetchPendingPriceRequests();
            fetchPendingAdminRequests();
        } else {
            fetchStaffNotifs();
            fetchStaffAdminRequestNotifs();
        }

        const channel = supabase
            .channel('credential-change-requests-notifs')
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'credential_change_requests' },
                () => {
                    if (!isSuperAdmin) fetchStaffNotifs();
                }
            )
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'menu_price_change_requests' },
                () => {
                    if (isSuperAdmin) fetchPendingPriceRequests();
                }
            )
            .on( // Add Menu / Add Variant / Add Modifier requests
                'postgres_changes',
                { event: '*', schema: 'public', table: 'admin_requests' },
                () => {
                    if (isSuperAdmin) fetchPendingAdminRequests();
                    else fetchStaffAdminRequestNotifs();
                }
            )
            .subscribe();



        // Profile completion doesn't need realtime — light periodic check is enough
        const interval = setInterval(() => {
            checkProfileCompletion();
        }, 60_000);

        return () => {
            supabase.removeChannel(channel);
            clearInterval(interval);
        };
    }, [fetchStaffNotifs, fetchStaffAdminRequestNotifs, checkProfileCompletion, isSuperAdmin, fetchPendingPriceRequests, fetchPendingAdminRequests]);




    useEffect(() => {
        const handleClick = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const dismissNotif = (id) => {
        const dismissed = JSON.parse(localStorage.getItem('dismissedNotifs') || '[]');
        if (!dismissed.includes(id)) dismissed.push(id);
        localStorage.setItem('dismissedNotifs', JSON.stringify(dismissed));
        setStaffNotifs(prev => prev.filter(n => n.id !== id));
    };

    const dismissAdminReqNotif = (id) => {
        const dismissed = JSON.parse(localStorage.getItem('dismissedAdminReqNotifs') || '[]');
        if (!dismissed.includes(id)) dismissed.push(id);
        localStorage.setItem('dismissedAdminReqNotifs', JSON.stringify(dismissed));
        setStaffAdminRequestNotifs(prev => prev.filter(n => n.id !== id));
    };


    const dismissAll = () => {
        const ids = staffNotifs.map(n => n.id);
        const dismissed = JSON.parse(localStorage.getItem('dismissedNotifs') || '[]');
        localStorage.setItem('dismissedNotifs', JSON.stringify([...dismissed, ...ids]));
        setStaffNotifs([]);

        const adminReqIds = staffAdminRequestNotifs.map(n => n.id);
        const dismissedAdminReq = JSON.parse(localStorage.getItem('dismissedAdminReqNotifs') || '[]');
        localStorage.setItem('dismissedAdminReqNotifs', JSON.stringify([...dismissedAdminReq, ...adminReqIds]));
        setStaffAdminRequestNotifs([]);

        clearAllStockCountAlerts();
        clearAllEODAlerts();
        clearAllAdjustmentRequests();
        clearAllAdjustmentResolutions();
        clearAllNewOrderNotifs();
        clearAllLowStockAlerts();
        clearAllRemakeAlerts();

        if (visibleOrderNotifs.length > 0) {
            onClearOrderNotifs?.();
        }
        setDismissedOrderIds(new Set());

        if (showProfileReminder) {
            sessionStorage.setItem('profileReminderDismissed', 'true');
            setProfileDismissed(true);
        }

        setShowAllStaffNotifs(false);
        setIsOpen(false);
    };

    const dismissProfileReminder = (e) => {
        e.stopPropagation();
        sessionStorage.setItem('profileReminderDismissed', 'true');
        setProfileDismissed(true);
    };

    const handleGoToProfile = () => {
        setIsOpen(false);
        if (typeof onGoToProfile === 'function') onGoToProfile();
    };

    const handleGoToMenuManagement = () => {
        setIsOpen(false);
        navigateToMenuManagement();
    };


    const applyChangesToProfile = async (request) => {
        const { requester_id, requester_type, requested_changes } = request;
        const profileData = {
            user_id: requester_id,
            user_type: requester_type,
            first_name: requested_changes.firstName,
            last_name: requested_changes.lastName,
            phone_number: requested_changes.phoneNumber,
            birthday: requested_changes.birthday || null,
            address: requested_changes.address,
            city: requested_changes.city,
            state_province: requested_changes.state,
            zip_code: requested_changes.zipCode,
            country: requested_changes.country,
            profile_picture_url: requested_changes.profilePictureUrl,
            updated_at: new Date().toISOString(),
        };
        const { data: existing } = await supabase
            .from('profiles').select('id')
            .eq('user_id', requester_id).eq('user_type', requester_type).single();
        if (existing) {
            const { error } = await supabase.from('profiles').update(profileData)
                .eq('user_id', requester_id).eq('user_type', requester_type);
            if (error) throw error;
        } else {
            const { error } = await supabase.from('profiles').insert([profileData]);
            if (error) throw error;
        }
        // NOTE: staff.first_name/last_name/phone_number now mirror the
        // assigned branch's Branch Owner (see BranchManagement) and are no
        // longer the individual staff member's own identity — so personal
        // profile-change requests must not write back into the staff table.
    };

    const showProfileReminder = missingFields.length > 0 && !profileDismissed;
    const propOrderNotifs = newOrderNotifs.filter(o => !dismissedOrderIds.has(o.order_id));
    const visibleOrderNotifs = staffNewOrders.length > 0 ? staffNewOrders : propOrderNotifs;
    const badgeCount = priceRequests.length + adminMenuRequests.length + staffNotifs.length + staffAdminRequestNotifs.length + visibleOrderNotifs.length + staffAdjustmentRequests.length + staffAdjustmentResolutions.length + staffCountAlerts.length + staffEODAlerts.length + staffRemakeAlerts.length + adminLowStockAlerts.length + (showProfileReminder ? 1 : 0);

    const STAFF_PANEL_LIMIT = 5;

    // Merge every staff notification category into one flat list sorted by
    // recency, so the newest item — of ANY type — always renders first.
    const getTs = (val) => val ? new Date(val).getTime() : 0;

    const staffNotificationItems = [
        ...visibleOrderNotifs.map((order, i) => ({
            type: 'order', ts: getTs(order.created_at), key: `order-${order.order_id ?? i}`, data: order, index: i,
        })),
        ...staffAdjustmentRequests.map(req => ({
            type: 'adjustment', ts: getTs(req.created_at), key: `adj-${req.id}`, data: req,
        })),
        ...staffAdjustmentResolutions.map(req => ({
            type: 'adjustment_resolution', ts: getTs(req.updated_at || req.created_at), key: `adjres-${req.id}`, data: req,
        })),
        ...staffCountAlerts.map(alert => ({
            type: 'count', ts: getTs(alert.created_at), key: `count-${alert.staff_count_id}`, data: alert,
        })),
        ...staffEODAlerts.map(alert => ({
            type: 'eod', ts: getTs(alert.created_at), key: `eod-${alert.id}`, data: alert,
        })),
        ...staffRemakeAlerts.map(req => ({
            type: 'remake', ts: getTs(req.created_at), key: `remake-${req.remake_request_id}`, data: req,
        })),
        ...staffNotifs.map(notif => ({
            type: 'profile', ts: getTs(notif.reviewed_at), key: `notif-${notif.id}`, data: notif,
        })),
        ...staffAdminRequestNotifs.map(notif => ({
            type: 'admin_request', ts: getTs(notif.created_at), key: `adminreq-${notif.id}`, data: notif,
        })),
    ].sort((a, b) => b.ts - a.ts);

    const totalStaffNotifCount = staffNotificationItems.length;
    const visibleStaffItems = showAllStaffNotifs
        ? staffNotificationItems
        : staffNotificationItems.slice(0, STAFF_PANEL_LIMIT);

    const renderStaffNotifItem = (item) => {
        switch (item.type) {
            case 'order': {
                const order = item.data;
                const idKey = order.order_id ?? item.index;
                return (
                    <div
                        key={item.key}
                        onClick={() => { markNotifClicked(`order-${idKey}`); navigateToOrders(); onGoToOrders?.(); setIsOpen(false) }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.click()}
                        className={`relative px-3 py-3 sm:px-5 sm:py-4 border-b flex items-start gap-2 sm:gap-3 transition-colors cursor-pointer ${clickedNotifIds.has(`order-${idKey}`) ? 'border-gray-100 bg-gray-50 opacity-60' : 'border-orange-50 bg-orange-50/60 hover:bg-orange-100'}`}
                    >
                        <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-orange-200 flex items-center justify-center shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                <path fill="#EA580C" d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2M1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96C5 16.1 5.9 17 7 17h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49A1.003 1.003 0 0 0 20 4H5.21l-.94-2z" />
                            </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-orange-900">
                                New order #{order.reference_number}
                            </p>
                            <p className="text-xs text-orange-700 mt-0.5">
                                {order.order_source === 'mobile_app' ? 'Mobile Customer' : 'Walk-in'} ·{' '}
                                {order.fulfillment_type?.replace('_', '-')} ·{' '}
                                ₱{parseFloat(order.final_amount || 0).toFixed(2)}
                            </p>
                            <p className="text-[11px] text-orange-500 mt-1">Tap to view orders</p>
                        </div>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (order.order_id != null) dismissNewOrderNotif(order.order_id);
                                setDismissedOrderIds(prev => {
                                    const next = new Set(prev);
                                    next.add(idKey);
                                    return next;
                                });
                            }}
                            className="text-orange-300 hover:text-orange-500 transition-colors shrink-0 mt-0.5"
                            title="Dismiss"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>
                );
            }
            case 'adjustment': {
                const req = item.data;
                return (
                    <div
                        key={item.key}
                        className={`relative px-3 py-3 sm:px-5 sm:py-4 border-b flex items-start gap-2 sm:gap-3 transition-colors ${clickedNotifIds.has(`adj-${req.id}`) ? 'border-gray-100 bg-gray-50 opacity-60' : 'border-blue-50 bg-blue-50/50 hover:bg-blue-100'}`}
                    >
                        <button
                            onClick={() => { markNotifClicked(`adj-${req.id}`); navigateToInventory('adjustment_requests'); setIsOpen(false) }}
                            className="flex-1 text-left flex items-start gap-2 sm:gap-3"
                        >
                            <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-blue-200 flex items-center justify-center shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                    <path fill="#1D4ED8" d="m14.06 9l.94.94L5.92 19H5v-.92zm3.6-6c-.25 0-.51.1-.7.29l-1.83 1.83l3.75 3.75l1.83-1.83c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29m-3.6 3.19L3 17.25V21h3.75L17.81 9.94z" />
                                </svg>
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-blue-900">
                                    {req.requested_by} requested a {req.adjustment_type === 'add' ? 'stock addition' : 'stock deduction'}
                                </p>
                                <p className="text-xs text-blue-700 mt-0.5">
                                    {req.ingredient_name} · {req.quantity} {req.ingredient_unit} · {req.reason}
                                </p>
                                <p className="text-[11px] text-blue-500 mt-1">Tap to review</p>
                            </div>
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); dismissAdjustmentNotif(req.id); }}
                            className="text-blue-300 hover:text-blue-500 transition-colors shrink-0 mt-0.5"
                            title="Dismiss (request stays pending in Inventory)"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>
                );
            }
            case 'adjustment_resolution': {
                const req = item.data;
                const approved = req.status === 'approved';
                return (
                    <div
                        key={item.key}
                        className={`relative px-3 py-3 sm:px-5 sm:py-4 border-b flex items-start gap-2 sm:gap-3 transition-colors ${clickedNotifIds.has(`adjres-${req.id}`) ? 'border-gray-100 bg-gray-50 opacity-60' : approved ? 'border-green-50 bg-green-50/50 hover:bg-green-100' : 'border-red-50 bg-red-50/50 hover:bg-red-100'}`}
                    >
                        <button
                            onClick={() => { markNotifClicked(`adjres-${req.id}`); navigateToInventory('adjustment_requests'); setIsOpen(false) }}
                            className="flex-1 text-left flex items-start gap-2 sm:gap-3"
                        >
                            <div className={`w-7 h-7 sm:w-9 sm:h-9 rounded-full flex items-center justify-center shrink-0 ${approved ? 'bg-green-200' : 'bg-red-200'}`}>
                                {approved ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                )}
                            </div>
                            <div className="min-w-0">
                                <p className={`text-sm font-semibold ${approved ? 'text-green-900' : 'text-red-900'}`}>
                                    Your {req.adjustment_type === 'add' ? 'addition' : 'deduction'} request was {approved ? 'approved' : 'rejected'}
                                </p>
                                <p className={`text-xs mt-0.5 ${approved ? 'text-green-700' : 'text-red-700'}`}>
                                    {req.ingredient_name} · {req.quantity} {req.ingredient_unit} · {req.reason}
                                </p>
                                <p className={`text-[11px] mt-1 ${approved ? 'text-green-500' : 'text-red-500'}`}>Tap to view</p>
                            </div>
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); dismissAdjustmentResolution(req.id); }}
                            className={`transition-colors shrink-0 mt-0.5 ${approved ? 'text-green-300 hover:text-green-500' : 'text-red-300 hover:text-red-500'}`}
                            title="Dismiss"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>
                );
            }
            case 'count': {
                const alert = item.data;
                return (
                    <div
                        key={item.key}
                        className={`px-3 py-3 sm:px-5 sm:py-4 border-b flex items-start gap-2 sm:gap-3 transition-colors ${clickedNotifIds.has(`count-${alert.staff_count_id}`) ? 'border-gray-100 bg-gray-50 opacity-60' : 'border-purple-50 bg-purple-50/40'}`}
                    >
                        <button
                            onClick={() => { markNotifClicked(`count-${alert.staff_count_id}`); navigateToInventory('stock_count_review'); setIsOpen(false) }}
                            className="flex-1 text-left flex items-start gap-2 sm:gap-3"
                        >
                            <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-purple-200 flex items-center justify-center shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                    <path fill="#7C3AED" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2m-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6m7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z" />
                                </svg>
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-purple-900">
                                    {alert.counted_by} submitted a {alert.count_type === 'beginning' ? 'beginning-of-shift' : alert.count_type === 'end_of_shift' ? 'end-of-shift' : 'general'} count
                                </p>
                                <p className="text-xs text-purple-700 mt-0.5">
                                    {alert.ingredient_name} · counted {alert.counted_quantity} {alert.ingredient_unit}
                                    {alert.discrepancy !== 0 && ` · variance ${alert.discrepancy > 0 ? '+' : ''}${alert.discrepancy}`}
                                </p>
                            </div>
                        </button>
                        <button
                            onClick={() => dismissStockCountAlert(alert.staff_count_id)}
                            className="text-purple-300 hover:text-purple-500 transition-colors shrink-0 mt-0.5"
                            title="Dismiss"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>
                );
            }
            case 'eod': {
                const alert = item.data;
                const failed = alert.status !== 'sent';
                return (
                    <div
                        key={item.key}
                        className={`px-3 py-3 sm:px-5 sm:py-4 border-b flex items-start gap-2 sm:gap-3 ${failed ? 'border-red-50 bg-red-50/40' : 'border-teal-50 bg-teal-50/40'}`}
                    >
                        <div className="flex-1 flex items-start gap-2 sm:gap-3">
                            <div className={`w-7 h-7 sm:w-9 sm:h-9 rounded-full flex items-center justify-center shrink-0 ${failed ? 'bg-red-200' : 'bg-teal-200'}`}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                    <path fill={failed ? '#DC2626' : '#0D9488'} d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2m0 4l-8 5l-8-5V6l8 5l8-5z" />
                                </svg>
                            </div>
                            <div className="min-w-0">
                                <p className={`text-sm font-semibold ${failed ? 'text-red-900' : 'text-teal-900'}`}>
                                    {failed ? 'EOD report failed to send' : 'EOD report sent'}
                                </p>
                                <p className={`text-xs mt-0.5 ${failed ? 'text-red-700' : 'text-teal-700'}`}>
                                    {alert.branch_name} · {alert.report_date}
                                    {failed && alert.error_message ? ` · ${alert.error_message}` : ''}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => dismissEODAlert(alert.id)}
                            className={`transition-colors shrink-0 mt-0.5 ${failed ? 'text-red-300 hover:text-red-500' : 'text-teal-300 hover:text-teal-500'}`}
                            title="Dismiss"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>
                );
            }
           case 'remake': {
                const req = item.data;
                const submitter = req.submitted_by_barista
                    ? `${req.submitted_by_barista.first_name} ${req.submitted_by_barista.last_name}`
                    : 'A barista';
                const responsible = (req.responsible_staff || []).map(s => `${s.name} (${s.role})`).join(', ');
                return (
                    <div
                        key={item.key}
                        className={`relative px-3 py-3 sm:px-5 sm:py-4 border-b flex items-start gap-2 sm:gap-3 transition-colors ${clickedNotifIds.has(`remake-${req.remake_request_id}`) ? 'border-gray-100 bg-gray-50 opacity-60' : 'border-amber-50 bg-amber-50/50 hover:bg-amber-100'}`}
                    >
                        <button
                             onClick={() => { markNotifClicked(`remake-${req.remake_request_id}`); navigateToInventory('remake_activity'); setIsOpen(false) }}
                            className="flex-1 text-left flex items-start gap-2 sm:gap-3"
                        >
                            <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-amber-200 flex items-center justify-center shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 12a9 9 0 1 1-3.5-7.1M21 3v6h-6" />
                                </svg>
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-amber-900">
                                    {submitter} requested a remake
                                </p>
                                <p className="text-xs text-amber-700 mt-0.5">
                                    Order #{req.reference_number || req.order_id} · {req.reason}
                                </p>
                                {responsible && (
                                    <p className="text-xs text-amber-600 mt-0.5 truncate">Responsible: {responsible}</p>
                                )}
                                <p className="text-[11px] text-amber-500 mt-1">
                                     {req.status === 'pending' ? 'Pending' : req.status === 'in_progress' ? 'Being processed in POS' : req.status} · Tap to view details
                                </p>
                            </div>
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); dismissRemakeAlert(req.remake_request_id); }}
                            className="text-amber-300 hover:text-amber-500 transition-colors shrink-0 mt-0.5"
                            title="Dismiss"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>
                );
            }
            case 'profile': {
                const notif = item.data;
                const isApproved = notif.status === 'approved';                
                return (
                    <div key={item.key} className="px-3 py-3 sm:px-5 sm:py-4 border-b border-gray-100 last:border-0">
                        <div className="flex items-start gap-2 sm:gap-3">
                            <div className={`w-7 h-7 sm:w-9 sm:h-9 rounded-full flex items-center justify-center shrink-0 ${isApproved ? 'bg-green-100' : 'bg-red-100'}`}>
                                {isApproved ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-900">
                                    Profile change {isApproved ? 'approved' : 'rejected'}
                                </p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    {isApproved
                                        ? 'Your profile has been updated successfully.'
                                        : `Reason: ${notif.reviewer_note || 'No reason provided.'}`
                                    }
                                </p>
                                {notif.reviewed_at && (
                                    <p className="text-xs text-gray-400 mt-1">
                                        {new Date(notif.reviewed_at).toLocaleDateString('en-US', {
                                            month: 'short', day: 'numeric', year: 'numeric',
                                            hour: '2-digit', minute: '2-digit'
                                        })}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={() => dismissNotif(notif.id)}
                                className="text-gray-300 hover:text-gray-500 transition-colors shrink-0 mt-0.5"
                                title="Dismiss"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                    </div>
                );
            }
            case 'admin_request': {
                const notif = item.data;
                const isApproved = notif.status === 'approved';
                const typeLabel = notif.item_type === 'variant' ? 'Variant' : notif.item_type === 'modifier' ? 'Modifier' : 'Item';
                const actionLabel = notif.request_type === 'add' ? 'addition' : notif.request_type === 'reinstate' ? 'reinstate' : 'deletion';
                return (
                    <div key={item.key} className="px-3 py-3 sm:px-5 sm:py-4 border-b border-gray-100 last:border-0">
                        <div className="flex items-start gap-2 sm:gap-3">
                            <div className={`w-7 h-7 sm:w-9 sm:h-9 rounded-full flex items-center justify-center shrink-0 ${isApproved ? 'bg-green-100' : 'bg-red-100'}`}>
                                {isApproved ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-900">
                                    {typeLabel} {actionLabel} {isApproved ? 'approved' : 'rejected'}
                                </p>
                                <p className="text-xs text-gray-500 mt-0.5 truncate">
                                    "{notif.item_name}"
                                </p>
                                {!isApproved && notif.rejection_reason && (
                                    <p className="text-xs text-red-500 mt-0.5 truncate" title={notif.rejection_reason}>
                                        Reason: {notif.rejection_reason}
                                    </p>
                                )}
                                {notif.created_at && (
                                    <p className="text-xs text-gray-400 mt-1">
                                        {new Date(notif.created_at).toLocaleDateString('en-US', {
                                            month: 'short', day: 'numeric', year: 'numeric'
                                        })}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={() => dismissAdminReqNotif(notif.id)}
                                className="text-gray-300 hover:text-gray-500 transition-colors shrink-0 mt-0.5"
                                title="Dismiss"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                    </div>
                );
            }
            default:
                return null;
        }
    };

    return (
        <div className="relative" ref={panelRef}>
            {/* ── Bell button ── */}
            <button
                onClick={() => { setIsOpen(o => !o); setShowAllStaffNotifs(false); }}
                className="header-icon-btn relative"
                title={isSuperAdmin ? 'Profile change requests' : 'Notifications'}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                    <g fill="none" fillRule="evenodd">
                        <path d="m12.594 23.258l-.012.002l-.071.035l-.02.004l-.014-.004l-.071-.036q-.016-.004-.024.006l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.016-.018m.264-.113l-.014.002l-.184.093l-.01.01l-.003.011l.018.43l.005.012l.008.008l.201.092q.019.005.029-.008l.004-.014l-.034-.614q-.005-.019-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.003-.011l.018-.43l-.003-.012l-.01-.01z" />
                        <path fill="currentColor" d="M5 9a7 7 0 0 1 14 0v3.764l1.822 3.644A1.1 1.1 0 0 1 19.838 18h-3.964a4.002 4.002 0 0 1-7.748 0H4.162a1.1 1.1 0 0 1-.984-1.592L5 12.764zm5.268 9a2 2 0 0 0 3.464 0zM12 4a5 5 0 0 0-5 5v3.764a2 2 0 0 1-.211.894L5.619 16h12.763l-1.17-2.342a2 2 0 0 1-.212-.894V9a5 5 0 0 0-5-5" />
                    </g>
                </svg>
                {badgeCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                        {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                )}
            </button>

            {/* ── Dropdown panel ── */}
            {isOpen && (
                <div
                    className="fixed sm:absolute inset-x-3 sm:inset-x-auto sm:right-0 top-16 sm:top-12 w-auto sm:w-[380px] max-w-[420px] mx-auto sm:mx-0 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden text-[13px] sm:text-sm"
                    style={{ maxHeight: '75vh', display: 'flex', flexDirection: 'column' }}
                >
                    {/* ════════════════════════════════
                        STAFF PANEL
                    ════════════════════════════════ */}
                    {!isSuperAdmin && (
                        <>
                            {/* Header */}
                            <div className="px-3 py-3 sm:px-5 sm:py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                                <div>
                                    <h3 className="text-sm sm:text-base font-semibold text-gray-900">Notifications</h3>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        {badgeCount > 0 ? `${badgeCount} unread` : 'All caught up'}
                                    </p>
                                </div>
                                {(staffNotifs.length > 0 || staffAdminRequestNotifs.length > 0 || staffCountAlerts.length > 0 || staffEODAlerts.length > 0 || staffAdjustmentRequests.length > 0 || staffRemakeAlerts.length > 0 || visibleOrderNotifs.length > 0 || showProfileReminder) && (
                                    <button onClick={dismissAll} className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
                                        Mark all read
                                    </button>
                                )}
                            </div>

                            {/* Body */}
                            <div className="overflow-y-auto flex-1">
                                {/* Profile reminder */}
                                {showProfileReminder && (
                                    <ProfileReminderCard
                                        missingFields={missingFields}
                                        onGoToProfile={handleGoToProfile}
                                        onDismiss={dismissProfileReminder}
                                    />
                                )}

                                {/* Empty state */}
                                {staffNotificationItems.length === 0 && !showProfileReminder && (
                                    <div className="flex flex-col items-center justify-center py-12 text-center px-6">
                                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                        </div>
                                        <p className="text-sm font-medium text-gray-600">No new notifications</p>
                                        <p className="text-xs text-gray-400 mt-1">You'll be notified when your request is reviewed.</p>
                                    </div>
                                )}

                                {/* Flat, newest-first notification list across all types */}
                                {visibleStaffItems.map(renderStaffNotifItem)}

                                {!showAllStaffNotifs && totalStaffNotifCount > STAFF_PANEL_LIMIT && (
                                    <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 text-center">
                                        <button
                                            onClick={() => setShowAllStaffNotifs(true)}
                                            className="text-xs text-[#A0826D] hover:underline font-medium"
                                        >
                                            View all notifications ({totalStaffNotifCount}) →
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* ════════════════════════════════
                        SUPER ADMIN PANEL
                    ════════════════════════════════ */}
                    {isSuperAdmin && (
                        <>
                            {/* Header */}
                            <div className="px-3 py-3 sm:px-5 sm:py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                                <div>
                                    <h3 className="text-sm sm:text-base font-semibold text-gray-900">Notifications</h3>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        {loading ? 'Loading...' : `${badgeCount} pending`}
                                    </p>
                                </div>
                                {(priceRequests.length > 0 || adminMenuRequests.length > 0) && (
                                    <button onClick={() => { dismissAllPriceRequests(); dismissAllAdminRequests(); }} className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
                                        Mark all read
                                    </button>
                                )}
                            </div>

                            {showProfileReminder && (
                                <ProfileReminderCard
                                    missingFields={missingFields}
                                    onGoToProfile={handleGoToProfile}
                                    onDismiss={dismissProfileReminder}
                                />
                            )}

                            {/* Scrollable body */}
                            <div className="overflow-y-auto flex-1">
                                <>
                                    {loading && (
                                        <div className="flex items-center justify-center py-10">
                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#FF6F3C]" />
                                        </div>
                                    )}

                                    {!loading && priceRequests.length > 0 && (
                                        <div>
                                            <div className="px-3 py-1.5 sm:px-5 sm:py-2 bg-emerald-50 border-b border-emerald-100">
                                                <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">
                                                    Price Change Requests
                                                </span>
                                            </div>
                                            {priceRequests.map(req => (
                                                <div
                                                    key={req.id}
                                                    className={`relative px-3 py-3 sm:px-5 sm:py-4 border-b flex items-start gap-2 sm:gap-3 transition-colors ${clickedNotifIds.has(`price-${req.id}`) ? 'border-gray-100 bg-gray-50 opacity-60' : 'border-emerald-50 hover:bg-emerald-50'}`}
                                                >
                                                    <button
                                                        onClick={() => { markNotifClicked(`price-${req.id}`); handleGoToMenuManagement(); }}
                                                        className="flex-1 text-left flex items-start gap-2 sm:gap-3"
                                                    >
                                                        <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                                                <path fill="#059669" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                                                            </svg>
                                                        </div>
                                                        <div className="min-w-0">
                                                            <div className="flex items-center gap-1.5">
                                                                <p className="text-sm font-semibold text-gray-900 truncate">{req.item_name}</p>
                                                                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 shrink-0">
                                                                    {req.item_type?.replace('_', ' ')}
                                                                </span>
                                                            </div>
                                                            <p className="text-xs text-gray-500 mt-0.5">
                                                                ₱{parseFloat(req.current_price).toFixed(2)} → <span className="font-semibold text-emerald-700">₱{parseFloat(req.requested_price).toFixed(2)}</span>
                                                                {!req.branch_id && <span className="ml-1 text-amber-600">(unassigned branch)</span>}
                                                            </p>
                                                            <p className="text-[11px] text-emerald-600 mt-1">Tap to review</p>
                                                        </div>
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); dismissPriceRequestNotif(req.id); }}
                                                        className="text-emerald-300 hover:text-emerald-500 transition-colors shrink-0 mt-0.5"
                                                        title="Dismiss"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {!loading && adminMenuRequests.length > 0 && (
                                        <div>
                                            <div className="px-3 py-1.5 sm:px-5 sm:py-2 bg-sky-50 border-b border-sky-100 flex items-center justify-between">
                                                <span className="text-xs font-semibold text-sky-700 uppercase tracking-wide">
                                                    Menu / Variant / Modifier Requests
                                                </span>
                                                <button onClick={dismissAllAdminRequests} className="text-xs text-sky-400 hover:text-sky-600">
                                                    Clear
                                                </button>
                                            </div>
                                            {adminMenuRequests.map(req => (
                                                <div
                                                    key={req.id}
                                                    className={`relative px-3 py-3 sm:px-5 sm:py-4 border-b flex items-start gap-2 sm:gap-3 transition-colors ${clickedNotifIds.has(`admreq-${req.id}`) ? 'border-gray-100 bg-gray-50 opacity-60' : 'border-sky-50 hover:bg-sky-50'}`}
                                                >
                                                    <button
                                                        onClick={() => { markNotifClicked(`admreq-${req.id}`); handleGoToMenuManagement(); }}
                                                        className="flex-1 text-left flex items-start gap-2 sm:gap-3"
                                                    >
                                                        <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-sky-100 flex items-center justify-center shrink-0">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                                                <path fill="#0284C7" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z" />
                                                            </svg>
                                                        </div>
                                                        <div className="min-w-0">
                                                            <div className="flex items-center gap-1.5">
                                                                <p className="text-sm font-semibold text-gray-900 truncate">{req.item_name}</p>
                                                                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-sky-100 text-sky-700 shrink-0 capitalize">
                                                                    {req.item_type}
                                                                </span>
                                                            </div>
                                                            <p className="text-xs text-gray-500 mt-0.5">
                                                                {req.request_type === 'add' ? 'New request' : req.request_type === 'reinstate' ? 'Reinstate request' : 'Deletion request'}
                                                                {req.branches?.name ? ` · ${req.branches.name.replace('Coffee Gus·to ', '')}` : ''}
                                                                {' · '}{new Date(req.created_at).toLocaleDateString('en-PH', { month: 'short', day: 'numeric' })}
                                                            </p>
                                                            <p className="text-[11px] text-sky-600 mt-1">Tap to review in Menu Management</p>
                                                        </div>
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); dismissAdminRequestNotif(req.id); }}
                                                        className="text-sky-300 hover:text-sky-500 transition-colors shrink-0 mt-0.5"
                                                        title="Dismiss"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {!loading && adminLowStockAlerts.length > 0 && (
                                        <div>
                                            <div className="px-3 py-1.5 sm:px-5 sm:py-2 bg-red-50 border-b border-red-100 flex items-center justify-between">
                                                <span className="text-xs font-semibold text-red-700 uppercase tracking-wide">
                                                    Low Stock Alerts
                                                </span>
                                                <button onClick={clearAllLowStockAlerts} className="text-xs text-red-400 hover:text-red-600">
                                                    Clear
                                                </button>
                                            </div>
                                            {adminLowStockAlerts.map(item => (
                                                <div
                                                    key={item.id}
                                                    className={`relative px-3 py-3 sm:px-5 sm:py-4 border-b flex items-start gap-2 sm:gap-3 transition-colors ${clickedNotifIds.has(`stock-${item.id}`) ? 'border-gray-100 bg-gray-50 opacity-60' : 'border-red-50 hover:bg-red-50'}`}
                                                >
                                                    <button
                                                        onClick={() => { markNotifClicked(`stock-${item.id}`); navigateToInventory('stock_list'); setIsOpen(false) }}
                                                        className="flex-1 text-left flex items-start gap-2 sm:gap-3"
                                                    >
                                                        <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                                                <path fill="#DC2626" d="M1 21h22L12 2zm12-3h-2v-2h2zm0-4h-2v-4h2z" />
                                                            </svg>
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-semibold text-gray-900 truncate">
                                                                {item.ingredient_name}
                                                                {item.available_stock <= 0 && (
                                                                    <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700 align-middle">Out of Stock</span>
                                                                )}
                                                            </p>
                                                            <p className="text-xs text-gray-500 mt-0.5">
                                                                {item.available_stock} {item.ingredient_unit} available · reorder at {item.reorder_level} {item.ingredient_unit}
                                                            </p>
                                                            <p className="text-[11px] text-red-600 mt-1">Tap to review inventory</p>
                                                        </div>
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); dismissLowStockAlert(item.id); }}
                                                        className="text-red-300 hover:text-red-500 transition-colors shrink-0 mt-0.5"
                                                        title="Dismiss"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* ✅ Empty state only when no requests, no price requests, AND no reminder */}
                                    {!loading && priceRequests.length === 0 && adminMenuRequests.length === 0 && adminLowStockAlerts.length === 0 && !showProfileReminder && (
                                        <div className="flex flex-col items-center justify-center py-12 text-center px-6">
                                            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="20 6 9 17 4 12" />
                                                </svg>
                                            </div>
                                            <p className="text-sm font-medium text-gray-600">All caught up!</p>
                                            <p className="text-xs text-gray-400 mt-1">No pending requests.</p>
                                        </div>
                                    )}
                                </>
                                {/* View all link at bottom of staff notifications */}
                                {staffNotifs.length > 0 && (
                                    <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 text-center">
                                        <button
                                            onClick={() => {
                                                // Navigate to a full notifications page if you have one
                                                // For now, just close the dropdown
                                                setIsOpen(false);
                                            }}
                                            className="text-xs text-[#A0826D] hover:underline font-medium"
                                        >
                                            View all notifications →
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function Header({ title, subtitle, onGoToProfile, newOrderNotifs, onClearOrderNotifs, onGoToOrders, onGoToInventory }) {
    return (
        <div className="header-root" style={{ minWidth: 0 }}>
            <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                <h1
                    className="header-title"
                    style={{
                        fontSize: 'clamp(1rem, 2.2vw, 1.5rem)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {title}
                </h1>
                <p
                    className="header-subtitle"
                    style={{
                        fontSize: 'clamp(0.7rem, 1.4vw, 0.875rem)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {subtitle}
                </p>
            </div>
            <div className="header-actions" style={{ flexShrink: 0 }}>


                <NotificationBell onGoToProfile={onGoToProfile}
                    newOrderNotifs={newOrderNotifs}
                    onClearOrderNotifs={onClearOrderNotifs}
                    onGoToOrders={onGoToOrders}
                    onGoToInventory={onGoToInventory} />

                <div className="header-brand" style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexShrink: 1 }}>

                    <img
                        src="https://hbylyyfhigscnrozlxqe.supabase.co/storage/v1/object/public/Background/CG%20LOGO%20only.png"
                        alt="Coffee Gusto Logo"
                        className="header-brand-logo"
                        style={{ width: 'clamp(40px, 8vw, 56px)', height: 'clamp(40px, 8vw, 56px)', flexShrink: 0 }}
                    />
                    <div style={{ minWidth: 0, overflow: 'hidden' }}>
                        <p
                            className="header-brand-name"
                            style={{
                                fontSize: 'clamp(0.75rem, 1.6vw, 1rem)',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            Coffee Gus·to
                        </p>
                        <p
                            className="header-brand-date"
                            style={{
                                fontSize: 'clamp(0.6rem, 1.2vw, 0.75rem)',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {new Date().toLocaleDateString('en-US', {
                                weekday: 'long', month: 'short', day: 'numeric', year: 'numeric'
                            })}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Header;